import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import SeoTags from "@/components/SeoTags";
import { Button } from "@/components/ui/button";
import type { SeoMetadata } from "@shared/seo";
import { ArrowRight, Mail, Scale, Shield } from "lucide-react";
import { Link } from "wouter";

export type LegalDocumentSection = {
  id: string;
  title: string;
  summary: string;
  paragraphs: string[];
  bullets?: string[];
};

type LegalDocumentProps = {
  metadata: SeoMetadata;
  badge: string;
  title: string;
  intro: string;
  effectiveDate: string;
  lastUpdated: string;
  sections: LegalDocumentSection[];
  contactEmail: string;
  contactLabel: string;
  relatedLinks: Array<{
    label: string;
    href: string;
  }>;
};

export default function LegalDocument({
  metadata,
  badge,
  title,
  intro,
  effectiveDate,
  lastUpdated,
  sections,
  contactEmail,
  contactLabel,
  relatedLinks,
}: LegalDocumentProps) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoTags metadata={metadata} />
      <Navbar />

      <main className="flex-1 pt-6 pb-16 sm:pt-8">
        <section className="relative overflow-hidden border-b border-border bg-[linear-gradient(180deg,rgba(47,102,212,0.08),rgba(15,23,42,0.03)_45%,transparent)]">
          <div className="container py-10 sm:py-14">
            <div className="max-w-4xl">
              <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                {badge}
              </span>
              <h1 className="mt-4 text-4xl font-semibold tracking-[-0.05em] text-slate-950 sm:text-5xl">
                {title}
              </h1>
              <p className="mt-4 max-w-3xl text-base leading-7 text-muted-foreground sm:text-lg">
                {intro}
              </p>
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              {relatedLinks.map((item) => (
                <Button key={item.href} asChild size="lg" variant="outline" className="rounded-full px-6">
                  <Link href={item.href}>
                    {item.label}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              ))}
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              <div className="rounded-[1.4rem] border border-border bg-background/90 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  Effective Date
                </p>
                <p className="mt-2 text-sm font-medium text-foreground">
                  {effectiveDate}
                </p>
              </div>
              <div className="rounded-[1.4rem] border border-border bg-background/90 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  Last Updated
                </p>
                <p className="mt-2 text-sm font-medium text-foreground">
                  {lastUpdated}
                </p>
              </div>
              <div className="rounded-[1.4rem] border border-border bg-background/90 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  Questions
                </p>
                <a
                  href={`mailto:${contactEmail}`}
                  className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-primary transition-colors hover:text-primary/80"
                >
                  {contactLabel}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="container py-8 sm:py-12">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)] lg:items-start">
            <aside className="space-y-5 lg:sticky lg:top-28">
              <div className="rounded-[1.6rem] border border-border bg-card p-6 shadow-[0_18px_40px_rgba(15,23,42,0.05)]">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    {badge.toLowerCase().includes("privacy") ? (
                      <Shield className="h-5 w-5" />
                    ) : (
                      <Scale className="h-5 w-5" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      On this page
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Jump directly to any section.
                    </p>
                  </div>
                </div>

                <nav className="mt-5 space-y-2">
                  {sections.map((section, index) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="flex items-start gap-3 rounded-2xl px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-primary"
                    >
                      <span className="mt-0.5 inline-flex min-w-7 justify-center rounded-full bg-muted px-2 py-1 text-[11px] font-semibold text-foreground">
                        {index + 1}
                      </span>
                      <span>{section.title}</span>
                    </a>
                  ))}
                </nav>
              </div>

              <div className="rounded-[1.6rem] border border-border bg-card p-6 shadow-[0_18px_40px_rgba(15,23,42,0.05)]">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      Need clarification?
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Reach the team directly for policy questions.
                    </p>
                  </div>
                </div>

                <a
                  href={`mailto:${contactEmail}`}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  {contactLabel}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </aside>

            <div className="space-y-5">
              {sections.map((section, index) => (
                <article
                  key={section.id}
                  id={section.id}
                  className="scroll-mt-28 rounded-[1.8rem] border border-border bg-card p-6 shadow-[0_18px_40px_rgba(15,23,42,0.04)] sm:p-8"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                      Section {index + 1}
                    </span>
                    <h2 className="text-2xl font-semibold tracking-[-0.04em] text-slate-950">
                      {section.title}
                    </h2>
                  </div>

                  <p className="mt-4 text-sm font-medium leading-6 text-slate-700 sm:text-base">
                    {section.summary}
                  </p>

                  <div className="mt-5 space-y-4 text-sm leading-7 text-muted-foreground sm:text-base">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>

                  {section.bullets?.length ? (
                    <ul className="mt-5 space-y-3 rounded-[1.35rem] border border-border bg-muted/35 p-5 text-sm leading-6 text-muted-foreground">
                      {section.bullets.map((bullet) => (
                        <li key={bullet} className="flex gap-3">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
